import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RxMinus, RxPlus } from "react-icons/rx";

import { SetCodeEnum } from "@sorcery/db";
import { Button, Line, LoadingSpinner, ModalBody, ModalHeader, P, toast } from "@sorcery/ui";

import { CollectionListPendingCard } from "~/components/organisms/collection/collectionlistpending/CollectionListPendingCard";
import { useModal } from "~/providers/ModalProvider";
import { searchJotai } from "~/state/searchJotai";
import type { MinimalCardType } from "~/types/card";
import { api } from "~/utils/trpc";

const setSlugToNameMap: Record<string, string> = {
  [SetCodeEnum.alp]: "Alpha",
  [SetCodeEnum.bet]: "Beta",
  [SetCodeEnum.art]: "Arthurian Legends",
};
export const CollectionBulkAddModal = () => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const search = useAtomValue(searchJotai)!;
  const [error, setError] = useState("");
  const { closeModal } = useModal();
  const [total, setTotal] = useState<number>();
  const [fetched, setFetched] = useState(false);

  const {
    mutateAsync: bulkAddApi,
    error: updateError,
    isPending,
  } = api.collection.bulkAdd.useMutation({
    onSuccess: () => {
      toast("Collection updated");
      void trpcUtils.collection.getCollectionlistBySession.invalidate();
      void trpcUtils.collection.search.invalidate();
      closeModal();
    },
  });

  const trpcUtils = api.useUtils();

  const { data, isFetching } = api.card.searchAll.useQuery(
    {
      query: search.query,
      sort: search.csort,
      set: search.set,
      filters: search.filters,
    },
    {
      enabled: !fetched,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const [cards, setCards] = useState<MinimalCardType[]>([]);

  useEffect(() => {
    if (data) {
      setCards(data);
      setFetched(true);
    }
  }, [data]);

  useEffect(() => {
    setTotal(
      cards.reduce(
        (acc, curr) => acc + curr.variants.reduce((to, th) => to + ((th as any).quantity ?? 1), 0),
        0,
      ) ?? 0,
    );
  }, [cards]);

  const loading = useMemo(() => {
    return isFetching;
  }, [isFetching]);

  // Track any trpc errors for display
  useEffect(() => {
    if (updateError) {
      // don't expose server side error messages
      setError("Something went wrong.");
    }
  }, [updateError]);

  const onTickAllDown = useCallback(() => {
    // Create a new array with the updated quantity without mutating the original state
    const newCards = cards.map((card) => ({
      ...card,
      variants: card.variants.map((i) => ({
        ...i,
        quantity: ((i as any).quantity ?? 1) - 1 > -1 ? ((i as any).quantity ?? 1) - 1 : 0,
      })),
    }));

    const updatedCards = newCards.filter(
      (c) => c.variants.reduce((a, c) => a + ((c as any).quantity ?? 1), 0) > 0,
    );
    setCards(updatedCards);
  }, [cards]);

  const onTickAllUp = useCallback(() => {
    // Create a new array with the updated quantity without mutating the original state
    const newCards = cards.map((card) => ({
      ...card,
      variants: card.variants.map((i) => ({
        ...i,

        quantity: ((i as any).quantity ?? 1) + 1,
      })),
    }));

    const updatedCards = newCards.filter((c) => c.variants.length > 0);
    setCards(updatedCards);
  }, [cards]);

  const onUpdateVariantQuantity = useCallback(
    (variantId: string, newQuantity: number) => {
      // Create a new array with the updated quantity without mutating the original state
      const cardsWithNewVairants = cards.map((card) => ({
        ...card,
        variants: card.variants
          .filter((v) => (v.id === variantId ? newQuantity > 0 : true))
          .map((i) => (i.id === variantId ? { ...i, quantity: newQuantity } : i)),
      }));

      const updatedCards = cardsWithNewVairants.filter((c) => c.variants.length > 0);
      setCards(updatedCards);
    },
    [cards],
  );

  const onSubmit = () => {
    // Assuming you have an API function to handle the update
    void bulkAddApi({
      cards: cards.map((c) => ({
        cardId: c.id,
        variants: c.variants.map((v) => ({
          variantId: v.id,

          quantity: (v as any).quantity ?? 1,
        })),
      })),
    });
  };

  return (
    <div className="p-6">
      {/* header */}
      <ModalHeader title="Bulk Add to Collection" />

      <ModalBody>
        {/* item */}

        <P>Would you like to bulk add all results from the following query to your collection?</P>

        {!!search.query && <P className="!mb-0">Query: {search.query}</P>}
        {search.set !== "*" && <P className="!mb-0">Set: {setSlugToNameMap[search.set]}</P>}
        {!!search.filters.length && (
          <P className="!mb-0">
            <span>Fliters:</span>{" "}
            <div className="ml-1 inline-flex flex-wrap">
              {search.filters.map((f) => (
                <span key={f.label} className="mb-1">
                  <div className="bg-info mr-2 inline rounded px-2.5 py-0.5 text-sm text-white">
                    <span>
                      {f.label} {f.comparitor ? `${f.comparitor} ${f.value}` : ""}
                    </span>
                  </div>
                </span>
              ))}
            </div>
          </P>
        )}
        <Line className="my-4 !bg-line-color" />

        {/* results */}
        {loading ? (
          <div className="h-8">
            <div className="mt-6">
              <LoadingSpinner />
            </div>
          </div>
        ) : cards.length === 0 ? (
          <>No Results</>
        ) : (
          <>
            {/* biome-ignore lint/style/noUnusedTemplateLiteral: <explanation> */}
            <div className={`max-h-[240px] overflow-y-scroll`}>
              {cards.map((c) => (
                <CollectionListPendingCard key={c.id} card={c} onChange={onUpdateVariantQuantity} />
              ))}
            </div>

            {!!total && (
              <>
                <Line className="mb-4 mt-6 !bg-line-color" />
                <div className="flex  justify-between">
                  <P className="!mb-0">Total: {total}</P>
                  <div className="flex items-center">
                    <span className="pr-1">(Update All)</span>
                    <div className="cursor-pointer px-2">
                      <RxMinus
                        size={16}
                        className="hover:text-primary mr-2 inline text-white transition-colors animate-in fade-in"
                        onClick={() => onTickAllDown()}
                      />
                      <RxPlus
                        size={17}
                        onClick={() => onTickAllUp()}
                        className="hover:text-primary inline text-white transition-colors animate-in fade-in"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {error && (
          <div className="text-red my-4" role="alert">
            <p>
              <span className="font-bold">Error. </span>
              {error}
              <span>.</span>
            </p>
          </div>
        )}
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button disabled={isPending} onClick={onSubmit} className="min-w-[5rem]">
          {isPending ? <>Adding...</> : <>Add</>}
        </Button>
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
